// @givmo/board — line 8b's question, with the committee's members and charter beside it
// (CBG-YR6-AUTH, frame c6; `board-committees.md` §8).
//
// The plan's "the module presents the question with the committee roster and charter documents
// attached and records the organization's own answer plus the provision it cites" (§1.4), held to
// §3.2 Band 3:
//   * the question is the IRS's, in its words, with where they are written;
//   * the facts beside it are facts: who is on the committee, which are directors, whether each votes
//     on it as the organization recorded it, and its charter;
//   * the three answers start with none chosen — the second and third are line 1a's own two kinds of
//     authority (Q4) — and "Where this comes from" is required, as for every recorded rule;
//   * nothing here reads the charter, and nothing says what an answer means for the return.
// Recording a new answer supersedes the one in force and keeps it in the committee's history.
// Recording is `board_governance_parameter:manage`, the board's own rules' string.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeAuthorityAnswer,
  type BoardCommitteeOut,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  TextField,
  toast,
} from '@givmo/design-system';
import { Link } from '../../board-routing';
import { FieldNote } from '../../components/field-note';
import { HowThisWorks } from '../../components/fine-print';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import { committeeOnlyWords as co } from './committee-only-words';
import { activeMembers, isCommitteeOnlyMember, ruleInForce } from './committees-decode';
import { committeesWords as w } from './committees-words';

const SECTION_LABEL = 'font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary';
const PLAIN = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const ANSWERS: readonly BoardCommitteeAuthorityAnswer[] = ['none', 'particular_matters', 'broad'];

/** The charter as the committee's page read it: the version in force, or none. */
export interface CharterInForce {
  recordId: string;
  title: string;
  adoptedOn: string | null;
}

export function AuthorityDialog({
  orgRef,
  committee,
  charter,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  charter: CharterInForce | null;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const [choice, setChoice] = useState<BoardCommitteeAuthorityAnswer | ''>('');
  const [citation, setCitation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const replacing = ruleInForce(committee, 'authority') !== null;
  const members = activeMembers(committee);

  const record = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerRecordBoardCommitteeRule({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: {
          rule_kind: 'authority',
          basis: 'choice',
          choice_value: choice,
          citation: citation.trim(),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.answerRecorded);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.answerFailed),
  });
  const pending = record.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={w.dialogTitle(committee.name)}
        className="flex flex-col gap-16 overflow-hidden"
        testId="committee-authority-dialog"
      >
        <Dialog.Title className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {w.dialogTitle(committee.name)}
        </Dialog.Title>

        <div
          className="-mx-4 flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto overscroll-contain px-4 py-2"
          data-testid="committee-authority-body"
        >
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {w.questionLead}{' '}
            <HowThisWorks
              testId="committee-authority-instructions"
              label={w.whatInstructionsSay}
              lines={w.instructionLines}
            />
          </p>

          <div className="flex flex-col gap-6" data-testid="committee-authority-members">
            <span className={SECTION_LABEL}>{w.attachedMembers}</span>
            {members.length === 0 ? (
              <span className={PLAIN}>{w.noMembers}</span>
            ) : (
              <ul className="flex flex-col">
                {members.map((member) => (
                  <li
                    key={member.id}
                    className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2 border-t border-border-subtle py-6 first:border-t-0"
                  >
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {member.display_name}
                    </span>
                    <span className={PLAIN}>
                      {[
                        ...(member.role === 'chair' ? [w.roleChair] : []),
                        // CBG-YR6-OUTSIDE (not drawn): line 1a's own question is whether any of the
                        // committee's members aren't on the governing body; such a member says so.
                        isCommitteeOnlyMember(member)
                          ? co.notOnTheBoard
                          : member.is_director
                            ? w.director
                            : w.notADirector,
                        ...(member.votes ? [] : [w.doesNotVote]),
                      ].join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-6" data-testid="committee-authority-charter">
            <span className={SECTION_LABEL}>{w.attachedCharter}</span>
            {charter === null ? (
              <span className={PLAIN}>{w.noCharter}</span>
            ) : (
              <span className={PLAIN}>
                <Link
                  to="/records/$recordId"
                  params={{ recordId: charter.recordId }}
                  className="font-w600 text-brand-600 outline-none hover:text-brand-700 focus-visible:underline"
                >
                  {charter.title}
                </Link>
                {charter.adoptedOn ? ` · ${w.charterVersion(displayDate(charter.adoptedOn))}` : ''}
              </span>
            )}
          </div>

          <RadioTileGroup
            value={choice}
            onValueChange={(next) =>
              setChoice(
                ANSWERS.includes(next as BoardCommitteeAuthorityAnswer)
                  ? (next as BoardCommitteeAuthorityAnswer)
                  : '',
              )
            }
            aria-label={w.answerGroupLabel}
            className="flex flex-col gap-10"
            testId="committee-authority-choice"
          >
            <span className={SECTION_LABEL}>{w.answerGroupLabel}</span>
            {ANSWERS.map((answer) => (
              <RadioTile
                key={answer}
                value={answer}
                label={w.answerLabel(answer)}
                testId={`committee-authority-${answer}`}
              />
            ))}
          </RadioTileGroup>

          <div className="flex flex-col gap-6">
            <TextField
              label={w.citationLabel}
              value={citation}
              maxLength={200}
              disabled={pending}
              onChange={(event) => setCitation(event.target.value)}
              testId="committee-authority-citation"
            />
            <FieldNote>{w.citationNote}</FieldNote>
          </div>

          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {w.doesntRead} {w.recordsYours}
            {replacing ? ` ${w.replacesLine}` : ''}
          </p>
        </div>

        {error !== null ? (
          <ErrorMessage testId="committee-authority-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex shrink-0 justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onClose}
            testId="committee-authority-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={pending}
            disabled={pending || choice === '' || citation.trim().length === 0}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="committee-authority-save"
          >
            {w.recordTheAnswer}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
